import { ArrowLeft, Sparkles, MessageCircle, FileText, GitBranch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Input } from "../../components/tailwind/input";
import { Button } from "../../components/tailwind/button";
import { Textarea } from "../../components/tailwind/textarea";
import { WORKFLOW_TEMPLATES } from "./data/workflowTemplates";
import { workflowTheme } from "./utils/workflowBuilderTheme";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { usePageTitle } from "../../hooks/usePageTitle";

const TOTAL_STEPS = 3;

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3].map((stepNumber, index) => (
        <div key={stepNumber} className="flex items-center gap-2">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              step >= stepNumber ? "bg-[hsl(158,48%,36%)] text-white" : "bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-500"
            }`}
          >
            {stepNumber}
          </div>
          {index < 2 && (
            <div className="w-10 h-1 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
              <div
                className={`h-full bg-[#0F6E56] dark:bg-emerald-500 transition-all ${step > stepNumber ? "w-full" : "w-0"}`}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StepNavigation({ step, onBack, onNext, nextDisabled, nextLabel = "Next", secondaryButton }) {
  return (
    <div className={`flex items-center justify-between pt-8 mt-8 border-t ${workflowTheme.borderSubtle}`}>
      <Button variant="outline" onClick={onBack} className="min-w-[100px]">
        Back
      </Button>
      {step < TOTAL_STEPS ? (
        <div className="flex items-center gap-3">
          {secondaryButton}
          <Button
            className="min-w-[100px] bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold"
            onClick={onNext}
            disabled={nextDisabled}
          >
            {nextLabel}
          </Button>
        </div>
      ) : (
        <div className="min-w-[100px]" />
      )}
    </div>
  );
}

export default function CreateWorkFlow() {
  usePageTitle("Create Workflow");
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [startFromScratch, setStartFromScratch] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  const selectedTemplate = WORKFLOW_TEMPLATES.find((template) => template.id === selectedTemplateId);

  const builderState = {
    workflowName: name.trim(),
    templateId: selectedTemplateId
  };

  const handleSelectTemplate = (template) => {
    setStartFromScratch(false);
    setName((currentName) => {
      const trimmed = currentName.trim();
      const previousTemplate = WORKFLOW_TEMPLATES.find((t) => t.id === selectedTemplateId);
      const stillUsingPreviousSuggestion =
        previousTemplate && trimmed === previousTemplate.suggestedName.trim();
      if (!trimmed || stillUsingPreviousSuggestion) {
        return template.suggestedName;
      }
      return currentName;
    });
    setSelectedTemplateId(template.id);
  };

  const handleStartFromScratch = () => {
    setStartFromScratch(true);
    setSelectedTemplateId(null);
    setStep(2);
  };

  const goBackOneStep = () => {
    if (step === 2) {
      setStartFromScratch(false);
    }
    setStep((current) => current - 1);
  };

  const handleHeaderBack = () => {
    if (step === 1) {
      navigate("/whatsapp/workflows");
      return;
    }
    goBackOneStep();
  };

  const openBuilder = () => {
    navigate("/whatsapp/workflows/create/new", { state: builderState });
  };

  const handleGenerate = () => {
    if (!aiPrompt.trim()) return;
    setIsGenerating(true);

    setTimeout(() => {
      setIsGenerating(false);
      navigate("/whatsapp/workflows/new-flow-ai/edit", { state: builderState });
    }, 3000);
  };

  if (isGenerating) {
    return (
      <div className={`h-screen flex flex-col items-center justify-center ${workflowTheme.page}`}>
        <div className={`w-16 h-16 ${workflowTheme.surface} rounded-2xl shadow-sm border ${workflowTheme.border} flex items-center justify-center mb-6 relative`}>
          <Sparkles className="w-8 h-8 text-[#0F6E56] dark:text-emerald-400 animate-pulse" />
          <div className="absolute inset-0 border-2 border-[#0F6E56] dark:border-emerald-500 rounded-2xl animate-ping opacity-20" />
        </div>
        <h2 className={`text-xl font-bold ${workflowTheme.textPrimary} mb-2`}>AI is building your workflow...</h2>
        <p className={`${workflowTheme.textSecondary} max-w-sm text-center`}>
          Reading your goal and arranging triggers, messages, and logic nodes to create the perfect flow.
        </p>
      </div>
    );
  }

  return (
    <div className={`${workflowTheme.pageSolid} flex flex-col animate-fade-in`}>
      <header className={`h-16 border-b ${workflowTheme.border} flex items-center px-6 sticky top-0 ${workflowTheme.header} z-10`}>
        <Button
          variant="ghost"
          size="icon"
          className={`mr-4 ${workflowTheme.backIconButton}`}
          onClick={handleHeaderBack}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <StepIndicator step={step} />
      </header>

      <div className={waShell(whatsappPageShell.focusedInner, "flex-1 mx-auto py-12 px-6")}>
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-bottom-4">
            <h1 className={`text-3xl font-bold ${workflowTheme.textPrimary} mb-2`}>What do you want to do?</h1>
            <p className={`${workflowTheme.textSecondary} mb-8`}>Pick a starting template — you can customize everything in the builder.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {WORKFLOW_TEMPLATES.map((template) => {
                const isSelected = selectedTemplateId === template.id;
                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => handleSelectTemplate(template)}
                    className={`text-left border-2 rounded-xl p-6 transition-all h-full flex flex-col ${
                      isSelected
                        ? `${workflowTheme.selectBrand} shadow-sm`
                        : "border-gray-100 dark:border-slate-800 hover:border-[#0F6E56] dark:hover:border-emerald-600 hover:bg-[#F8F8F6] dark:hover:bg-slate-900"
                    }`}
                  >
                    <h3 className={`font-semibold mb-2 ${isSelected ? "text-[#085041] dark:text-emerald-300" : "text-gray-800 dark:text-gray-200"}`}>
                      {template.title}
                    </h3>
                    <p className={`text-sm ${workflowTheme.textSecondary} flex-1`}>{template.description}</p>
                  </button>
                );
              })}
            </div>

            <StepNavigation
              step={step}
              onBack={() => navigate("/whatsapp/workflows")}
              onNext={() => setStep(2)}
              nextDisabled={!selectedTemplateId}
              secondaryButton={
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleStartFromScratch}
                  className="border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-[#0F6E56] dark:hover:border-emerald-600"
                >
                  Start from Scratch
                </Button>
              }
            />
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-8 max-w-xl mx-auto">
            <h1 className={`text-3xl font-bold ${workflowTheme.textPrimary} mb-2 text-center`}>Name it and pick channel</h1>
            {startFromScratch ? (
              <p className={`${workflowTheme.textSecondary} mb-8 text-center`}>
                Starting with a <span className="font-medium text-gray-700 dark:text-gray-300">blank canvas</span>
              </p>
            ) : selectedTemplate ? (
              <p className={`${workflowTheme.textSecondary} mb-8 text-center`}>
                Template: <span className="font-medium text-gray-700 dark:text-gray-300">{selectedTemplate.title}</span>
              </p>
            ) : null}

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2`}>
                  What should we call this workflow?
                </label>
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Diwali Flash Sale"
                  className={`h-14 text-lg ${workflowTheme.input}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3`}>Which channel?</label>
                <div className="flex gap-3 flex-wrap">
                  <div className={`border-2 ${workflowTheme.selectBrand} px-4 py-3 rounded-lg flex items-center gap-2 font-semibold`}>
                    <MessageCircle className="w-5 h-5" /> WhatsApp
                  </div>
                  <div
                    className="border-2 border-gray-100 dark:border-slate-800 text-gray-400 dark:text-gray-500 px-4 py-3 rounded-lg flex items-center gap-2 font-semibold cursor-not-allowed bg-gray-50 dark:bg-slate-900"
                    title="Coming soon"
                  >
                    Instagram <span className="text-[10px] bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded ml-1">SOON</span>
                  </div>
                  <div
                    className="border-2 border-gray-100 dark:border-slate-800 text-gray-400 dark:text-gray-500 px-4 py-3 rounded-lg flex items-center gap-2 font-semibold cursor-not-allowed bg-gray-50 dark:bg-slate-900"
                    title="Coming soon"
                  >
                    Facebook <span className="text-[10px] bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded ml-1">SOON</span>
                  </div>
                </div>
              </div>
            </div>

            <StepNavigation
              step={step}
              onBack={goBackOneStep}
              onNext={() => setStep(3)}
              nextDisabled={!name.trim()}
              nextLabel="Continue"
            />
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-8">
            <h1 className={`text-3xl font-bold ${workflowTheme.textPrimary} mb-2 text-center`}>How do you want to build it?</h1>
            <p className={`${workflowTheme.textSecondary} mb-10 text-center`}>
              {startFromScratch
                ? "Open the builder on a blank canvas, or let AI generate a flow from a description."
                : "Open the builder with your template, or let AI refine the flow from a description."}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className={`border-2 border-[#0F6E56] dark:border-emerald-600 rounded-2xl p-6 ${workflowTheme.surface} shadow-lg relative overflow-hidden flex flex-col`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#E1F5EE] dark:bg-emerald-950/50 rounded-lg flex items-center justify-center">
                    <GitBranch className="w-5 h-5 text-[#0F6E56] dark:text-emerald-400" />
                  </div>
                  <h2 className={`text-xl font-bold ${workflowTheme.textPrimary}`}>
                    {startFromScratch ? "Open blank builder" : "Open builder with template"}
                  </h2>
                </div>
                <p className={`${workflowTheme.textSecondary} mb-6 flex-1`}>
                  {startFromScratch ? (
                    <>Open an empty canvas and build your flow from scratch. Add nodes, connections, and messages manually.</>
                  ) : (
                    <>
                      Start with the <span className="font-medium text-gray-700 dark:text-gray-300">{selectedTemplate?.title}</span> flow
                      pre-loaded on the canvas. Edit nodes, connections, and messages manually.
                    </>
                  )}
                </p>
                <Button className="w-full bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold mt-auto" onClick={openBuilder}>
                  Open Builder →
                </Button>
              </div>

              <div className={`border ${workflowTheme.border} rounded-2xl p-6 ${workflowTheme.surface} hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-md transition-all flex flex-col relative overflow-hidden`}>
                <div className="absolute top-0 right-0 bg-amber-500 dark:bg-amber-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
                  Coming soon
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <h2 className={`text-xl font-bold ${workflowTheme.textPrimary}`}>Describe your goal, AI builds it</h2>
                </div>

                <Textarea
                  placeholder="Tell us what this workflow should do in plain English..."
                  className={`h-32 ${workflowTheme.input} mb-2 resize-none`}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 p-2 rounded mb-4">
                  No knowledge base added — AI will generate from your description only. You can add documents for
                  better results.
                </p>

                <Button variant="outline" className={`w-full justify-start text-gray-600 dark:text-gray-400 mb-6 border-dashed ${workflowTheme.border}`}>
                  <FileText className="w-4 h-4 mr-2" /> + Add documents or website
                </Button>

                <Button
                  className="w-full bg-gray-900 hover:bg-black dark:bg-slate-700 dark:hover:bg-slate-600 text-white mt-auto"
                  onClick={handleGenerate}
                  disabled={!aiPrompt.trim()}
                >
                  Generate workflow →
                </Button>
              </div>
            </div>

            <StepNavigation step={step} onBack={() => setStep(2)} onNext={() => {}} nextDisabled />
          </div>
        )}
      </div>
    </div>
  );
}
